import CloseIcon from '@mui/icons-material/Close';
import { Alert, AlertTitle, IconButton, Slide, Stack } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import {
  NotificationProps as NotificationContextProps,
  useNotificationContext,
} from './NotificationContext';

interface NotificationProps extends NotificationContextProps {
  id: string;
}

export function Notification({
  id,
  title,
  timeout = 5000,
  severity,
  isClosable = false,
  children,
  autoClose = true,
  icon,
  action,
}: NotificationProps) {
  const { hideNotification } = useNotificationContext();
  const [slideOut, setSlideOut] = useState(false);

  const closeNotification = useCallback(() => {
    setSlideOut(true);
    hideNotification(id);
  }, [hideNotification, id]);

  useEffect(() => {
    if (!autoClose) {
      return;
    }
    const timer = setTimeout(() => {
      closeNotification();
    }, timeout);

    const slideOutTimer = setTimeout(() => {
      setSlideOut(true);
    }, timeout - 100);

    return () => {
      clearTimeout(timer);
      clearTimeout(slideOutTimer);
    };
  }, [timeout, closeNotification, autoClose]);

  if (severity === 'custom') {
    return (
      <Slide direction="right" in={!slideOut}>
        <Stack position="relative" width="fit-content">
          {children}
          {action}
          {isClosable && (
            <IconButton
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                width: 40,
                height: 40,
              }}
              size="small"
              onClick={closeNotification}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Stack>
      </Slide>
    );
  }

  return (
    <Slide direction="right" in={!slideOut}>
      <Alert
        severity={severity}
        icon={icon}
        sx={(theme) => ({
          border: '1px solid',
          borderColor: severity
            ? theme.palette[severity].main
            : theme.palette.text.primary,
        })}
        action={
          <>
            {action}
            {isClosable ? (
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={closeNotification}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            ) : null}
          </>
        }
      >
        {title && <AlertTitle>{title}</AlertTitle>}
        {children}
      </Alert>
    </Slide>
  );
}
